import { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { MapPin, Car, Bus, Navigation } from 'lucide-react'
import { siteData } from '../../data/siteData'

export default function MapSection() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  const directions = [
    { icon: Car, text: 'Autem: wjazd od ulicy Ogórkowej, parking bezpłatny bezpośrednio przy terenie' },
    { icon: Bus, text: 'Autobusem: linie do Tarnowa Opolskiego, dalej 5 min pieszo' },
    { icon: Navigation, text: 'Wpisz w GPS: ul. Ogórkowa, Tarnów Opolski' },
  ]

  return (
    <section className="relative py-24 sm:py-32 bg-section-dark grain-overlay" style={{ backgroundColor: 'rgba(10,14,10,0.55)', backdropFilter: 'blur(2px)' }}>
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-military/40 to-transparent" />

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="font-mono text-xs text-military-light tracking-[0.4em] uppercase">
            Lokalizacja
          </span>
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl text-bone mt-4 tracking-wider">
            JAK DO NAS DOJECHAĆ?
          </h2>
          <p className="font-body text-chrome mt-4 max-w-2xl mx-auto">
            Znajdziesz nas łatwo — nawet przez las.
          </p>
          <div className="w-24 h-0.5 bg-military-light mx-auto mt-6" />
        </motion.div>

        {/* Map */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative rounded-sm overflow-hidden border border-military/20"
        >
          <iframe
            src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2525.0!2d${siteData.coordinates.lng}!3d${siteData.coordinates.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTDCsDMzJzMzLjMiTiAxOMKwMDYnNDMuMiJF!5e0!3m2!1spl!2spl!4v1!5m2!1spl!2spl&maptype=roadmap&style=feature:all|element:geometry|color:0x1a1a1a|visibility:on&style=feature:all|element:labels.text.fill|color:0x8a8a8a&style=feature:all|element:labels.text.stroke|color:0x111111&style=feature:water|element:geometry|color:0x1a1a1a&style=feature:road|element:geometry|color:0x2a2a2a`}
            width="100%"
            height="450"
            style={{ border: 0, filter: 'grayscale(100%) invert(92%) contrast(83%)' }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Lokalizacja Strefa TOP"
            className="w-full"
          />
        </motion.div>

        {/* Open in Maps */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-center mt-6"
        >
          <a
            href="https://maps.app.goo.gl/5jKaYDJpLT8LrxVz7"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-3 bg-military-light text-bone font-subheading font-bold text-sm uppercase tracking-wider rounded-sm hover:bg-military-glow transition-colors"
          >
            <MapPin size={16} />
            Otwórz w Google Maps
          </a>
        </motion.div>

        {/* Directions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4"
        >
          {directions.map((dir, i) => {
            const Icon = dir.icon
            return (
              <div
                key={i}
                className="flex items-start gap-3 p-5 bg-steel border border-military/20 rounded-sm"
              >
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-military/10 rounded-sm">
                  <Icon size={16} className="text-military-light" />
                </div>
                <p className="font-body text-sm text-chrome-light leading-relaxed">
                  {dir.text}
                </p>
              </div>
            )
          })}
        </motion.div>
      </div>
    </section>
  )
}
